/**
 * @file capture/useDurableRecorder.ts
 * @brief MediaRecorder + IndexedDB chunk flush for web boardroom capture.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteDraft,
  draftAgendaIds,
  getDraft,
  newDraftId,
  appendChunk,
  putDraft,
  type BoardroomDraftBookmark,
  type BoardroomDraftMeta,
  type BoardroomDraftParticipant,
  type BoardroomMeetingTier,
} from "./boardroomDraftStore";
import type { AgendaCheck } from "../agendaChecks";
import { boardroomAudioConstraints } from "./captureProfile";
import { isQuotaExceeded, probeDurableStorage } from "./durabilityProbe";
import { CAPTURE_GAIN, createGainStage, type GainStage } from "./gainStage";
import { describeMicError, describeRecorderSupportError } from "./micErrors";
import { speechCeilingForGain, startMicMeter, type MicMeterHandle } from "./micMeter";
import { pickPreferredMime } from "./preferredMime";
import { mediaRecorderTimesliceMs, shouldUseSegmentHold } from "./safariDetect";
import { canCaptureSystemAudio, openSystemAudio } from "./systemAudio";

export type RecorderSetup = {
  userId: string;
  title: string;
  targetLanguages: string[];
  participants: BoardroomDraftParticipant[];
  deviceId?: string;
  /** Desktop app only: mix call audio (Zoom/Teams/Meet) in with the mic. */
  includeSystemAudio?: boolean;
  /** Resume an interrupted draft */
  resumeDraftId?: string;
  /** Stripe hold — required on fresh start; reused on resume */
  paymentIntentId?: string | null;
  authorizedCents?: number | null;
  /** Link recording to a scheduled meeting (Start now). */
  scheduledMeetingId?: string | null;
  /** GDPR recording notice confirmed before start (06-10-2026). */
  recordingNotice?: { method: "setup_slide" | "scheduled_confirm"; at: string } | null;
  /** Meeting Notes or full (ADR-030, 06-10-2026). Absent = full. */
  tier?: BoardroomMeetingTier;
  /** Agenda notes picked at setup, attached after the meeting is created. */
  prepNoteIds?: string[];
};

export type DurableRecorderState = {
  draftId: string | null;
  status: "idle" | "recording" | "held" | "stopping" | "error";
  elapsedMs: number;
  bookmarks: BoardroomDraftBookmark[];
  error: string | null;
  mimeLabel: string;
  /** The agenda notes this recording will attach when it uploads. */
  prepNoteIds: string[];
  /** B49: the agenda check boxes; null = none shown. */
  agendaChecks: AgendaCheck[] | null;
  /** The draft's tier (ADR-030): a resumed draft keeps its own, so the
   *  recorder's Notes chrome follows the draft, not the navigation. */
  tier: BoardroomMeetingTier;
};

export function useDurableRecorder() {
  const [state, setState] = useState<DurableRecorderState>({
    draftId: null,
    status: "idle",
    elapsedMs: 0,
    bookmarks: [],
    error: null,
    mimeLabel: "",
    prepNoteIds: [],
    agendaChecks: null,
    tier: "full",
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  /** Raw mic stream — the one holding the device open; must be stopped to release it. */
  const streamRef = useRef<MediaStream | null>(null);
  /** Call audio (desktop app only); stopped with the mic. */
  const systemStreamRef = useRef<MediaStream | null>(null);
  /** Gain/limiter graph in front of MediaRecorder; null when bypassed (Safari, no AudioContext). */
  const gainStageRef = useRef<GainStage | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const metaRef = useRef<BoardroomDraftMeta | null>(null);
  const tickRef = useRef<number | null>(null);
  const segmentStartedAtRef = useRef<number>(0);
  const baseElapsedRef = useRef<number>(0);
  const meterRef = useRef<MicMeterHandle | null>(null);
  /** Live 0..100 mic level for the listening glow (no React re-renders). */
  const audioLevelRef = useRef(0);
  const mimeTypeRef = useRef("");

  const clearTick = () => {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const stopMeter = () => {
    meterRef.current?.stop();
    meterRef.current = null;
    audioLevelRef.current = 0;
  };

  const persist = useCallback(async () => {
    const meta = metaRef.current;
    if (!meta) return;
    meta.lastActiveAt = Date.now();
    meta.elapsedMs =
      baseElapsedRef.current +
      (meta.status === "recording" ? Date.now() - segmentStartedAtRef.current : 0);
    try {
      // Metadata only: chunks are written once each, in `saveChunk`. Putting
      // the chunk array here rewrote the whole recording every 2s and crashed
      // a desktop meeting 14 minutes in (30-09-2026).
      await putDraft({ ...meta });
      setState((s) => ({ ...s, elapsedMs: meta.elapsedMs, error: s.error }));
    } catch (err) {
      setState((s) => ({
        ...s,
        elapsedMs: meta.elapsedMs,
        error: isQuotaExceeded(err)
          ? "Storage full — free disk space or End & Process now. New audio may not survive a refresh."
          : "Could not save draft locally. Keep this tab open and End & Process soon.",
      }));
    }
  }, []);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  /** Chunk writes still in flight; Stop waits for them so the draft is whole. */
  const pendingChunkWritesRef = useRef(new Set<Promise<void>>());
  /** A resume() is in flight; a second one waits its turn by doing nothing. */
  const resumingRef = useRef(false);

  /** Store one new chunk exactly once, at its position in the recording. */
  const saveChunk = (blob: Blob) => {
    const draftId = metaRef.current?.draftId;
    chunksRef.current.push(blob);
    if (!draftId) return;
    const seq = chunksRef.current.length - 1;
    const write = appendChunk(draftId, seq, blob)
      .then(() => persistRef.current())
      .catch((err: unknown) => {
        setState((s) => ({
          ...s,
          error: isQuotaExceeded(err)
            ? "Storage full — free disk space or End & Process now. New audio may not survive a refresh."
            : "Could not save draft locally. Keep this tab open and End & Process soon.",
        }));
      })
      .finally(() => {
        pendingChunkWritesRef.current.delete(write);
      });
    pendingChunkWritesRef.current.add(write);
  };

  const wireRecorder = (recorder: MediaRecorder) => {
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) {
        saveChunk(ev.data);
      }
    };
    recorder.onerror = () => {
      setState((s) => ({
        ...s,
        status: "error",
        error: "Recording failed. Your draft is still saved locally.",
      }));
    };
  };

  const beginTick = () => {
    clearTick();
    tickRef.current = window.setInterval(() => {
      const elapsed = baseElapsedRef.current + (Date.now() - segmentStartedAtRef.current);
      setState((s) => ({ ...s, elapsedMs: elapsed }));
      if (metaRef.current) {
        metaRef.current.elapsedMs = elapsed;
        metaRef.current.lastActiveAt = Date.now();
      }
    }, 250);
  };

  const createRecorder = (stream: MediaStream): MediaRecorder => {
    const preferred = pickPreferredMime();
    let recorder: MediaRecorder;
    try {
      recorder = preferred.mimeType
        ? new MediaRecorder(stream, { mimeType: preferred.mimeType })
        : new MediaRecorder(stream);
    } catch {
      recorder = new MediaRecorder(stream);
    }
    mimeTypeRef.current = recorder.mimeType || preferred.mimeType || preferred.contentType;
    wireRecorder(recorder);
    return recorder;
  };

  const stopTracks = () => {
    stopMeter();
    // Gain graph first, then the device — the shaper is reading from that stream.
    gainStageRef.current?.stop();
    gainStageRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    systemStreamRef.current?.getTracks().forEach((t) => t.stop());
    systemStreamRef.current = null;
    mediaRecorderRef.current = null;
  };

  const start = useCallback(
    async (setup: RecorderSetup): Promise<boolean> => {
      setState((s) => ({ ...s, error: null, status: "idle" }));

      const supportErr = describeRecorderSupportError();
      if (supportErr) {
        setState((s) => ({ ...s, status: "error", error: supportErr }));
        return false;
      }

      const durability = await probeDurableStorage();
      if (!durability.ok) {
        setState((s) => ({
          ...s,
          status: "error",
          error: durability.error,
        }));
        return false;
      }

      let draftId = setup.resumeDraftId ?? newDraftId();
      let existingChunks: Blob[] = [];
      let existingElapsed = 0;
      let existingBookmarks: BoardroomDraftBookmark[] = [];
      let existingAgendaChecks: AgendaCheck[] | null = null;
      let paymentIntentId = setup.paymentIntentId ?? null;
      let authorizedCents = setup.authorizedCents ?? null;
      let scheduledMeetingId = setup.scheduledMeetingId ?? null;
      let recordingNotice = setup.recordingNotice ?? null;
      // The tier chosen at setup; a resumed draft keeps its own (ADR-030).
      let tier: BoardroomMeetingTier = setup.tier === "notes" ? "notes" : "full";
      // Carried over like the payment hold and the schedule: a recording resumed
      // after a crash reaches the recorder with no nav state, so without this the
      // agenda chosen at setup was dropped on the floor. (24-08-2026)
      let prepNoteIds = [...(setup.prepNoteIds ?? [])];
      let includeSystemAudio = !!setup.includeSystemAudio;

      if (setup.resumeDraftId) {
        const existing = await getDraft(setup.resumeDraftId);
        if (existing) {
          draftId = existing.draftId;
          existingChunks = existing.chunks ?? [];
          existingElapsed = existing.elapsedMs ?? 0;
          existingBookmarks = existing.bookmarks ?? [];
          existingAgendaChecks = Array.isArray(existing.agendaChecks) ? existing.agendaChecks : null;
          if (existing.paymentIntentId) {
            paymentIntentId = existing.paymentIntentId;
            authorizedCents = existing.authorizedCents ?? null;
          }
          if (existing.scheduledMeetingId) {
            scheduledMeetingId = existing.scheduledMeetingId;
          }
          if (existing.recordingNotice) recordingNotice = existing.recordingNotice;
          if (existing.tier) tier = existing.tier === "notes" ? "notes" : "full";
          // Old drafts carry a single `prepNoteId`; draftAgendaIds reads both.
          const saved = draftAgendaIds(existing);
          if (saved.length > 0) {
            prepNoteIds = saved;
          }
          if (existing.includeSystemAudio) {
            includeSystemAudio = true;
          }
        }
      }
      // A draft started in the desktop app and continued in a browser tab can only
      // carry on with the mic; the flag is dropped so the draft says what it holds.
      if (includeSystemAudio && !canCaptureSystemAudio()) {
        includeSystemAudio = false;
      }

      const audioConstraint = boardroomAudioConstraints(setup.deviceId);

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraint,
        });
      } catch (err) {
        setState((s) => ({
          ...s,
          status: "error",
          error: describeMicError(err),
        }));
        return false;
      }

      // Call audio is opened BEFORE anything is recorded: if the user asked for it
      // and it is not there, recording mic-only would lose the whole remote side of
      // the call without anyone noticing until the transcript came back.
      let systemStream: MediaStream | null = null;
      if (includeSystemAudio) {
        try {
          systemStream = await openSystemAudio();
        } catch (err) {
          stream.getTracks().forEach((t) => t.stop());
          setState((s) => ({
            ...s,
            status: "error",
            error: err instanceof Error ? err.message : "Could not capture call audio.",
          }));
          return false;
        }
      }

      streamRef.current = stream;
      systemStreamRef.current = systemStream;
      chunksRef.current = existingChunks;
      baseElapsedRef.current = existingElapsed;
      segmentStartedAtRef.current = Date.now();

      // Boost distant speakers back up now that AGC is off. Null = bypassed, in
      // which case everything downstream just uses the raw stream unchanged.
      const gainStage = createGainStage(stream, CAPTURE_GAIN, systemStream);
      if (systemStream && !gainStage) {
        // The mix lives in the gain graph; without it the call would not be recorded.
        stopTracks();
        setState((s) => ({
          ...s,
          status: "error",
          error:
            "Could not mix call audio with the mic. Turn call audio off to record the mic only.",
        }));
        return false;
      }
      gainStageRef.current = gainStage;
      const captureStream = gainStage?.stream ?? stream;

      stopMeter();
      // Meter taps what is actually being written, so its ceiling follows the gain.
      const meter = startMicMeter(
        captureStream,
        audioLevelRef,
        speechCeilingForGain(gainStage?.gain ?? 1),
      );
      meterRef.current = meter;

      let recorder: MediaRecorder;
      try {
        recorder = createRecorder(captureStream);
      } catch (err) {
        // Meter was started above and owns an AudioContext — it leaked on this path.
        stopMeter();
        gainStage?.stop();
        gainStageRef.current = null;
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState((s) => ({
          ...s,
          status: "error",
          error:
            err instanceof Error ? err.message : "Could not start MediaRecorder in this browser.",
        }));
        return false;
      }

      const preferred = pickPreferredMime();
      const meta: BoardroomDraftMeta = {
        draftId,
        userId: setup.userId,
        title: setup.title,
        targetLanguages: setup.targetLanguages,
        participants: setup.participants,
        bookmarks: existingBookmarks,
        mimeType: recorder.mimeType || preferred.mimeType,
        contentType: preferred.contentType,
        extension: preferred.extension,
        elapsedMs: existingElapsed,
        createdAt: Date.now(),
        lastActiveAt: Date.now(),
        deviceId: setup.deviceId,
        includeSystemAudio,
        status: "recording",
        paymentIntentId,
        authorizedCents,
        scheduledMeetingId,
        recordingNotice,
        tier,
        prepNoteIds,
        agendaChecks: existingAgendaChecks,
      };
      metaRef.current = meta;

      mediaRecorderRef.current = recorder;
      recorder.start(mediaRecorderTimesliceMs());

      beginTick();

      setState({
        draftId,
        status: "recording",
        elapsedMs: existingElapsed,
        bookmarks: existingBookmarks,
        error: null,
        mimeLabel: mimeTypeRef.current || preferred.contentType,
        prepNoteIds,
        agendaChecks: existingAgendaChecks,
        tier,
      });

      try {
        await putDraft(meta);
      } catch (err) {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
        stopTracks();
        clearTick();
        setState((s) => ({
          ...s,
          status: "error",
          error: isQuotaExceeded(err)
            ? "Not enough storage to save a durable draft. Free disk space and try again."
            : "Could not create a durable draft. Leave private browsing and try again.",
        }));
        return false;
      }
      // Recording (21-10-2026): the caller releases a fresh card hold when
      // this is false, so a start that never recorded leaves no hold behind.
      return true;
    },
    [persist],
  );

  const hold = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    baseElapsedRef.current += Date.now() - segmentStartedAtRef.current;
    if (metaRef.current) metaRef.current.status = "held";
    clearTick();

    if (shouldUseSegmentHold()) {
      // Safari: stop this segment (keep stream) instead of pause/resume.
      await new Promise<void>((resolve) => {
        const finish = () => resolve();
        recorder.addEventListener("stop", finish, { once: true });
        try {
          recorder.requestData();
        } catch {
          /* ignore */
        }
        try {
          recorder.stop();
        } catch {
          finish();
        }
      });
      mediaRecorderRef.current = null;
    } else {
      try {
        recorder.pause();
      } catch {
        /* ignore */
      }
    }

    setState((s) => ({
      ...s,
      status: "held",
      elapsedMs: baseElapsedRef.current,
    }));
    await persist();
  }, [persist]);

  const resume = useCallback(async () => {
    const stream = streamRef.current;
    if (!stream) return;

    const existing = mediaRecorderRef.current;
    // Already recording: a second Resume (double click, a tap before React
    // re-rendered "held" away) must be a no-op. It used to fall through to the
    // segment restart below and start a SECOND MediaRecorder next to the live
    // one; both fed the same chunk list, so the draft held interleaved streams
    // that no decoder can play (14 starts in one 8-minute desktop test,
    // 30-09-2026).
    if (existing && existing.state === "recording") return;
    // Two taps can both get past that check while the first is still awaiting.
    if (resumingRef.current) return;
    resumingRef.current = true;
    try {
      if (metaRef.current) metaRef.current.status = "recording";
      segmentStartedAtRef.current = Date.now();

      if (existing && existing.state === "paused" && !shouldUseSegmentHold()) {
        try {
          existing.resume();
        } catch {
          /* fall through to segment restart */
        }
        if (mediaRecorderRef.current?.state === "recording") {
          beginTick();
          setState((s) => ({ ...s, status: "recording" }));
          await persist();
          return;
        }
      }

      // Never two live recorders: if the old one is still paused (its resume()
      // threw), let it flush its last chunk and stop before the next segment
      // starts, so segments stay one after another in the chunk list.
      const stale = mediaRecorderRef.current;
      if (stale && stale.state !== "inactive") {
        await new Promise<void>((resolve) => {
          stale.addEventListener("stop", () => resolve(), { once: true });
          try {
            stale.stop();
          } catch {
            resolve();
          }
        });
      }

      // Fresh MediaRecorder segment on the same live stream (Safari path). Must be
      // the PROCESSED stream — recording the raw one here would drop the gain from
      // everything after a pause and step the level mid-meeting.
      let recorder: MediaRecorder;
      try {
        recorder = createRecorder(gainStageRef.current?.stream ?? stream);
      } catch (err) {
        setState((s) => ({
          ...s,
          status: "error",
          error: err instanceof Error ? err.message : "Could not resume recording in this browser.",
        }));
        return;
      }
      mediaRecorderRef.current = recorder;
      recorder.start(mediaRecorderTimesliceMs());
      beginTick();
      setState((s) => ({
        ...s,
        status: "recording",
        mimeLabel: mimeTypeRef.current || s.mimeLabel,
      }));
      await persist();
    } finally {
      resumingRef.current = false;
    }
  }, [persist]);

  const addBookmark = useCallback(
    (type: "decision" | "todo") => {
      const timeInSecs = Math.max(0, Math.floor(state.elapsedMs / 1000));
      const bookmark: BoardroomDraftBookmark = {
        id: `bm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        type,
        timeInSecs,
        timestamp: new Date().toISOString(),
      };
      setState((s) => {
        const bookmarks = [...s.bookmarks, bookmark];
        if (metaRef.current) metaRef.current.bookmarks = bookmarks;
        return { ...s, bookmarks };
      });
      void persist();
    },
    [persist, state.elapsedMs],
  );

  /**
   * Change the agenda note mid-recording.
   *
   * Nothing is linked here — the meeting row does not exist until the upload
   * creates it — so this only decides what `submitBoardroomCapture` will attach
   * afterwards. It goes through the draft, not just React state, because a
   * recording recovered after a crash reads its agenda from there. (24-08-2026)
   */
  const setPrepNoteIds = useCallback(
    (noteIds: string[]) => {
      const next = [...new Set(noteIds.filter(Boolean))];
      if (metaRef.current) {
        metaRef.current.prepNoteIds = next;
        // The single field is retired; clear it so it can never resurrect a
        // note the user removed.
        metaRef.current.prepNoteId = null;
      }
      setState((s) => ({ ...s, prepNoteIds: next }));
      void persist();
    },
    [persist],
  );

  /**
   * B49: the agenda check boxes (the item list and its ticks). Into the draft
   * like the bookmarks, so a crash-recovered recording keeps them. Ignored
   * once the recording has stopped: ticks are frozen at Stop.
   */
  const setAgendaChecks = useCallback(
    (update: (prev: AgendaCheck[] | null) => AgendaCheck[] | null) => {
      const meta = metaRef.current;
      if (!meta || meta.status === "interrupted") return;
      const next = update(meta.agendaChecks ?? null);
      meta.agendaChecks = next;
      setState((s) => ({ ...s, agendaChecks: next }));
      void persist();
    },
    [persist],
  );

  /**
   * Rename the meeting while recording (DES 11-10-2026, the recorder's
   * editable heading). Into the draft too, so a recording recovered after a
   * crash keeps the new name, and the meeting is created with it.
   */
  const setTitle = useCallback(
    (title: string) => {
      const next = title.trim();
      if (!next) return;
      if (metaRef.current) metaRef.current.title = next;
      void persist();
    },
    [persist],
  );

  const stopAndAssemble = useCallback(async (): Promise<{
    blob: Blob;
    meta: BoardroomDraftMeta;
  } | null> => {
    setState((s) => ({ ...s, status: "stopping" }));
    clearTick();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        try {
          recorder.requestData();
        } catch {
          /* older browsers */
        }
        recorder.stop();
      });
    }
    stopTracks();

    if (metaRef.current?.status === "recording") {
      baseElapsedRef.current += Date.now() - segmentStartedAtRef.current;
    }
    const meta = metaRef.current;
    if (!meta) {
      setState((s) => ({ ...s, status: "idle" }));
      return null;
    }
    meta.elapsedMs = baseElapsedRef.current;
    meta.status = "interrupted";
    meta.bookmarks = state.bookmarks;
    // The final requestData() chunk is queued above; let every chunk land
    // before the caller uploads and then deletes the draft.
    await Promise.all([...pendingChunkWritesRef.current]);
    await putDraft(meta);

    const blob = new Blob(chunksRef.current, {
      type: meta.contentType || meta.mimeType || "audio/webm",
    });
    setState((s) => ({
      ...s,
      status: "idle",
      elapsedMs: meta.elapsedMs,
    }));
    return { blob, meta };
  }, [state.bookmarks]);

  const discard = useCallback(async () => {
    clearTick();
    // Forget the draft BEFORE stopping (07-10-2026): stop() hands over one
    // last chunk a moment later, and with the meta still here `saveChunk`
    // wrote it and `persist` put the meta back after the delete below, so a
    // discarded recording came back as "Interrupted boardroom". With no meta
    // and no handler, that late chunk goes nowhere.
    const id = metaRef.current?.draftId ?? state.draftId;
    metaRef.current = null;
    const recorder = mediaRecorderRef.current;
    if (recorder) recorder.ondataavailable = null;
    try {
      recorder?.stop();
    } catch {
      /* ignore */
    }
    stopTracks();
    // A chunk still being written would otherwise land after the delete and
    // leave an orphan row behind.
    await Promise.all([...pendingChunkWritesRef.current]);
    if (id) await deleteDraft(id);
    chunksRef.current = [];
    setState({
      draftId: null,
      status: "idle",
      elapsedMs: 0,
      bookmarks: [],
      error: null,
      mimeLabel: "",
      prepNoteIds: [],
      agendaChecks: null,
      tier: "full",
    });
  }, [state.draftId]);

  // beforeunload + tab hide — flush draft so force-quit / sleep still recover
  useEffect(() => {
    if (state.status !== "recording" && state.status !== "held") return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      void persist();
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") void persist();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onHidden);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onHidden);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [persist, state.status]);

  useEffect(
    () => () => {
      clearTick();
      stopTracks();
    },
    [],
  );

  return {
    state,
    start,
    hold,
    resume,
    addBookmark,
    setPrepNoteIds,
    setAgendaChecks,
    setTitle,
    stopAndAssemble,
    discard,
    persist,
    /** Live 0..100 mic RMS for ListeningMicButton glow (RAF-safe). */
    audioLevelRef,
  };
}
